/**
 * Converts a string to lower-kebab-case.
 *
 * For example, "Hello World" becomes "hello-world".
 */
export const toLowerKebabCase = (str: string) => {
  return str.toLowerCase().replace(/ /g, '-');
};

/**
 * Normalize a value that should be a plain string into one.
 *
 * STRUCTURAL TYPE COERCION FOR TIPTAP ATTRIBUTE AND FIRESTORE CONTENT READS.
 *
 * The four layers that the `code` attribute on `runnable-code`,
 * `runnable-karel`, and `runnable-graphics` nodes travel through
 * (TipTap, ProseMirror, `y-prosemirror`, y.js) do not enforce string type at
 * runtime. Verified by reading the installed source of each layer
 * (`@tiptap/core@2.27.2`, `prosemirror-model@1.25.4`, `y-prosemirror@1.3.7`)
 * and confirmed by `y-prosemirror` issue 116, which states that conversion
 * does not check attribute types. If any path ever lets a non string value
 * into the attribute (including a ProseMirror inline text node array of shape
 * `[{text}, {text}, ...]`), every downstream consumer will receive it,
 * stringify it via `Array.prototype.toString`, and render `[object Object]`.
 *
 * This helper closes the gap at every read edge that we control. Plain
 * strings pass through unchanged. Arrays whose elements carry a `text`
 * string are concatenated so any legacy value we encounter renders as
 * readable source. Any other shape falls back to an empty string, which
 * keeps the user from seeing garbled output like `[object Object]` even
 * for shapes we do not recognize. The empty string trade off is that
 * unfamiliar data is silently dropped at render time.
 *
 * It is applied at
 *   - The Firestore read surfaces for `projects/{projectId}/code/{fileId}.content`
 *     (`getProjectFilesCode`, `getProjectCode` for published code, and the
 *     `FireMonaco` `onSnapshot` handler before `monacoModel.setValue`).
 *   - The TipTap node view first render for `runnable-code`, `runnable-karel`,
 *     and `runnable-graphics` `code` attributes, plus the `renderText`
 *     serialization path on Karel.
 *   - The HTML parse and HTML render paths via the attribute level
 *     `parseHTML` and `renderHTML` on those three TipTap nodes, so any HTML
 *     round trip is coerced at the TipTap boundary too.
 *   - The duolessons TipTap tree walker in `tipTapParser.ts`, which extracts
 *     the `code` attribute of `runnable-code`, `runnable-karel`, and
 *     `runnable-graphics` nodes into lesson chat context.
 *
 * Known limitations
 *   - Does not recurse into nested ProseMirror node structures. It only
 *     handles the flat `[{text}, {text}, ...]` case that actually occurs.
 *   - Does not prove the original write path has been eliminated. The y.js
 *     and JSON deserialization paths cannot be closed without forking
 *     `y-prosemirror` or rewriting upstream behavior, so a defensive read
 *     remains the only coverage for that path.
 *   - Silently drops unexpected shapes by returning the empty string.
 *     Callers that need to observe such cases should check the input
 *     themselves before calling this.
 */
export const extractInlineText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (!Array.isArray(value)) return '';
  return value
    .map(node => {
      if (typeof node === 'string') return node;
      if (typeof node !== 'object' || node === null) return '';
      if (!('text' in node)) return '';
      if (typeof node.text !== 'string') return '';
      return node.text;
    })
    .join('');
};
